import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Zap } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const W = 320, H = 380, SW = 26, SH = 14, EW = 22, EH = 16, ECOLS = 6, EROWS = 3;

export default function SpaceShooter() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const move = useRef(0);
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("idle");

  const newEnemies = () => { const e = []; for (let r = 0; r < EROWS; r++) for (let c = 0; c < ECOLS; c++) e.push({ x: 30 + c * 46, y: 30 + r * 30, alive: true }); return e; };
  const start = () => { g.current = { sx: W / 2 - SW / 2, bullets: [], enemies: newEnemies(), edir: 1, estep: 0, cd: 0, wave: 1 }; scoreRef.current = 0; setScore(0); setStatus("playing"); sfx.beep(); };
  const fire = () => { const s = g.current; if (!s.bullets || s.cd > 0) return; s.bullets.push({ x: s.sx + SW / 2, y: H - 30 }); s.cd = 12; sfx.shoot(); };

  useEffect(() => {
    const kd = (e) => { if (["ArrowLeft", "a"].includes(e.key)) { move.current = -1; e.preventDefault(); } else if (["ArrowRight", "d"].includes(e.key)) { move.current = 1; e.preventDefault(); } else if (e.key === " ") { fire(); e.preventDefault(); } };
    const ku = (e) => { if (["ArrowLeft", "a", "ArrowRight", "d"].includes(e.key)) move.current = 0; };
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, []);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const draw = () => {
      ctx.fillStyle = "#0a0a18"; ctx.fillRect(0, 0, W, H);
      const s = g.current;
      ctx.fillStyle = "#a7f3d0"; ctx.fillRect(s.sx || 0, H - 24, SW, SH); ctx.fillRect((s.sx || 0) + SW / 2 - 2, H - 30, 4, 6);
      ctx.fillStyle = "#f77737"; (s.bullets || []).forEach((b) => ctx.fillRect(b.x - 2, b.y - 6, 4, 8));
      ctx.fillStyle = "#e8b04b"; (s.enemies || []).forEach((e) => { if (e.alive) { ctx.fillRect(e.x, e.y, EW, EH); ctx.fillStyle = "#c1121f"; ctx.fillRect(e.x + 4, e.y + 4, 4, 4); ctx.fillRect(e.x + EW - 8, e.y + 4, 4, 4); ctx.fillStyle = "#e8b04b"; } });
    };
    if (status !== "playing") { draw(); return; }
    let raf;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      if (s.cd > 0) s.cd--;
      s.sx = Math.max(0, Math.min(W - SW, s.sx + move.current * 5));
      s.bullets = s.bullets.filter((b) => { b.y -= 7; return b.y > -10; });
      s.estep++;
      if (s.estep >= Math.max(6, 24 - s.wave * 3)) {
        s.estep = 0;
        const alive = s.enemies.filter((e) => e.alive);
        if (alive.length) {
          const maxX = Math.max(...alive.map((e) => e.x)), minX = Math.min(...alive.map((e) => e.x));
          if ((maxX + EW + 6 > W && s.edir > 0) || (minX - 6 < 0 && s.edir < 0)) { s.edir *= -1; s.enemies.forEach((e) => (e.y += 14)); }
          else s.enemies.forEach((e) => (e.x += s.edir * 10));
        }
      }
      for (const b of s.bullets) { for (const e of s.enemies) { if (e.alive && b.x > e.x && b.x < e.x + EW && b.y > e.y && b.y < e.y + EH) { e.alive = false; b.y = -100; scoreRef.current += 10; setScore(scoreRef.current); sfx.hit(); } } }
      if (!s.enemies.some((e) => e.alive)) { s.wave++; s.enemies = newEnemies(); s.edir = 1; sfx.powerUp(); }
      if (s.enemies.some((e) => e.alive && e.y + EH >= H - 26)) { cancelAnimationFrame(raf); sfx.explode(); setStatus("over"); return; }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2"><AScore label="Score" value={score} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[300px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-3">
            {status === "over" && <AResult win={false} text={`Game Over · ${score}`} testId="shooter-over" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <PadButton testId="shooter-left" onPress={() => { move.current = -1; }} onRelease={() => { move.current = 0; }}><ArrowLeft className="h-5 w-5" /></PadButton>
        <PadButton testId="shooter-fire" onPress={fire} className="w-16 bg-kirana-red text-white"><Zap className="h-5 w-5" /></PadButton>
        <PadButton testId="shooter-right" onPress={() => { move.current = 1; }} onRelease={() => { move.current = 0; }}><ArrowRight className="h-5 w-5" /></PadButton>
      </div>
    </div>
  );
}
